import { useLayoutEffect, useRef } from "react";
import { ArrowLeft } from "lucide-react";
import type { CourseOverlap } from "../types/attendanceDialog";

// ============================================================================
// Types
// ============================================================================

export interface ConcurrentCoursePanelProps {
  courseOverlaps?: CourseOverlap[];
  concurrentCourseAttendance: any;
  transferredStudentIds: Set<string>;
  lessonId?: string;
  isTransferring: boolean;
  pendingTransfer: {
    studentId: string;
    studentName: string;
    courseName: string;
    courseLessonId: string;
  } | null;
  setPendingTransfer: (
    v: ConcurrentCoursePanelProps["pendingTransfer"],
  ) => void;
  transferReason: string;
  setTransferReason: (v: string) => void;
  onTransferConfirm: (studentId: string, fromLessonId: string) => Promise<void>;
  /** "overview" uses p-4 sizing, "edit" uses p-3 sizing */
  variant: "overview" | "edit";
}

// ============================================================================
// Component
// ============================================================================

export function ConcurrentCoursePanel({
  courseOverlaps,
  concurrentCourseAttendance,
  transferredStudentIds,
  lessonId,
  isTransferring,
  pendingTransfer,
  setPendingTransfer,
  transferReason,
  setTransferReason,
  onTransferConfirm,
  variant,
}: ConcurrentCoursePanelProps) {
  const transferReasonInputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (pendingTransfer) {
      transferReasonInputRef.current?.focus();
    }
  }, [pendingTransfer]);

  // Always use RPC data as sole source of truth for concurrent courses.
  // The courseOverlaps prop (from Klassenbuch grid) is ignored to ensure
  // consistent behavior (cancellation status, student lists) across all entry points.
  const overlapBoxes = concurrentCourseAttendance?.courses?.length
    ? concurrentCourseAttendance.courses.map((c: any) => ({
        courseName: c.course_name,
        courseRoom: c.room_name || null,
        teacherNames: c.teacher_names || null,
        lessonId: c.lesson_id,
        isCancelled: c.is_cancelled ?? false,
        students: c.students.map((s: any) => ({
          id: s.id,
          name: s.name || s.id,
        })),
      }))
    : [];

  if (overlapBoxes.length === 0) return null;

  const padding = variant === "overview" ? "p-4" : "p-3";
  const headingSize = variant === "overview" ? "" : "text-sm";

  return (
    <div className="flex flex-wrap gap-2">
      {overlapBoxes.map((co: any) => (
        <div
          key={co.lessonId || co.courseName}
          className={`flex-1 min-w-[200px] ${padding} rounded-lg border ${
            co.isCancelled
              ? "bg-white border-red-300"
              : "bg-amber-50 border-amber-200"
          }`}
        >
          <h4
            className={`font-medium mb-${
              variant === "overview" ? "1" : "0.5"
            } ${headingSize} ${
              co.isCancelled ? "text-red-800" : "text-amber-900"
            }`}
          >
            {co.isCancelled ? "⚠️" : "ℹ️"}{" "}
            <span className={co.isCancelled ? "line-through" : ""}>
              {co.courseName}
            </span>
            {co.isCancelled && (
              <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full no-underline">
                Entfällt
              </span>
            )}
            {(co.courseRoom || co.teacherNames) && (
              <span
                className={`ml-2 text-xs font-normal ${
                  co.isCancelled ? "text-gray-500" : "text-amber-700"
                }`}
              >
                ({[co.courseRoom && `Raum ${co.courseRoom}`, co.teacherNames].filter(Boolean).join(" · ")})
              </span>
            )}
          </h4>
          <div
            className={`text-xs ${
              co.isCancelled ? "text-gray-500" : "text-amber-800"
            }`}
          >
            {(() => {
              const courseData = concurrentCourseAttendance?.courses?.find(
                (c: any) =>
                  c.lesson_id === co.lessonId ||
                  c.course_name === co.courseName,
              );
              // Resolve the course lesson ID for transfer
              const courseLessonId = co.lessonId || courseData?.lesson_id;
              return co.students
                .filter((s: { id: string }) => !transferredStudentIds.has(s.id))
                .map((s: { id: string; name: string }) => {
                  const parts = s.name.trim().split(/\s+/);
                  const shortName =
                    parts.length >= 2
                      ? `${parts[0]} ${parts[parts.length - 1].slice(0, 2)}.`
                      : s.name;
                  // Find status from concurrent data
                  const studentStatus = courseData?.students?.find(
                    (cs: any) => cs.id === s.id,
                  )?.status;
                  const isPresent =
                    studentStatus === "present" ||
                    studentStatus === "late" ||
                    studentStatus === "late_excused" ||
                    studentStatus === "late_unexcused";
                  const isGuestToday = !!courseData?.students?.find(
                    (cs: any) => cs.id === s.id,
                  )?.is_guest;
                  return (
                    <div key={s.id}>
                      <div className="flex items-center justify-between gap-1 py-0.5">
                        <span className="inline-flex items-center gap-0.5 text-xs">
                          {studentStatus != null ? (
                            isPresent ? (
                              <span
                                className="text-green-600 font-bold"
                                title={`Anwesend (${studentStatus})`}
                              >
                                ✓
                              </span>
                            ) : (
                              <span
                                className="text-red-500 font-bold"
                                title={`Abwesend (${studentStatus})`}
                              >
                                ✗
                              </span>
                            )
                          ) : (
                            <span
                              className="text-gray-400"
                              title="Keine Anwesenheit erfasst"
                            >
                              –
                            </span>
                          )}
                          {shortName}
                          {isGuestToday && (
                            <span className="ml-1 inline-flex items-center rounded px-1 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700">
                              Gast heute
                            </span>
                          )}
                        </span>
                        {courseLessonId && lessonId && !co.isCancelled && (
                          <button
                            type="button"
                            disabled={isTransferring}
                            onClick={() => {
                              setPendingTransfer({
                                studentId: s.id,
                                studentName: s.name,
                                courseName: co.courseName,
                                courseLessonId,
                              });
                              setTransferReason("");
                            }}
                            className="px-2 py-0.5 rounded-full text-xs font-medium border border-amber-300 bg-amber-100 hover:bg-amber-200 text-amber-800 hover:text-amber-900 transition-colors whitespace-nowrap"
                            title={`${s.name} nimmt heute nicht in ${co.courseName} teil`}
                          >
                            heute nicht
                          </button>
                        )}
                      </div>
                      {/* Inline transfer confirm panel */}
                      {pendingTransfer?.studentId === s.id && (
                        <div className="mt-1 mb-2 p-2 bg-white border border-amber-300 rounded-lg shadow-sm">
                          <p className="text-xs text-amber-900 mb-1.5 font-medium">
                            {isPresent ? (
                              <>
                                {pendingTransfer!.studentName} wurde bereits
                                als{" "}
                                <strong>
                                  anwesend in {pendingTransfer!.courseName}{" "}
                                  eingetragen
                                </strong>
                                . Bitte bestätigen das{" "}
                                {pendingTransfer!.studentName.split(/\s+/)[0]}{" "}
                                doch in der Klasse bleibt!
                              </>
                            ) : (
                              <>
                                {pendingTransfer!.studentName} nimmt heute
                                nicht in {pendingTransfer!.courseName} teil.
                              </>
                            )}
                          </p>
                          <input
                            ref={transferReasonInputRef}
                            type="text"
                            placeholder="Grund eingeben"
                            value={transferReason}
                            onChange={(e) => setTransferReason(e.target.value)}
                            className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 mb-2 focus:outline-none focus:ring-1 focus:ring-amber-400"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={
                                isTransferring || !transferReason.trim()
                              }
                              onClick={() =>
                                onTransferConfirm(s.id, courseLessonId)
                              }
                              className="flex-1 text-xs bg-amber-500 text-white rounded px-2 py-1.5 font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
                            >
                              {isTransferring ? "Lädt..." : "Bestätigen"}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setPendingTransfer(null);
                                setTransferReason("");
                              }}
                              className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1.5"
                            >
                              Abbrechen
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                });
            })()}
          </div>
        </div>
      ))}
    </div>
  );
}
